/**
 * Настройки расчёта. Источник истины — сервер (он же сбрасывает кеш парка при
 * смене радиуса), но правки применяются мгновенно и переживают офлайн за счёт
 * локальной копии: на улице связь пропадает чаще, чем хотелось бы.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { PlannerSettings } from '@cdf/shared';

import { api } from '../api/client';

const STORAGE_KEY = 'cdf.settings.v1';
/** Ползунок шлёт десятки изменений подряд — сервер должен увидеть только итог. */
const PUSH_DELAY_MS = 600;

export type SettingsOverrides = Partial<PlannerSettings>;

/**
 * Оставляет только ключи, которые есть в дефолтах и совпадают по типу:
 * localStorage переживает деплои, а форма настроек — меняется.
 */
function sanitize(raw: unknown, base: PlannerSettings): SettingsOverrides {
  if (typeof raw !== 'object' || raw === null) return {};
  const source = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const key of Object.keys(base) as Array<keyof PlannerSettings>) {
    const value = source[key];
    if (value === undefined) continue;
    if (typeof value !== typeof base[key]) continue;
    if (typeof value === 'number' && !Number.isFinite(value)) continue;
    out[key] = value;
  }

  return out as SettingsOverrides;
}

function readStored(base: PlannerSettings): SettingsOverrides {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? sanitize(JSON.parse(raw), base) : {};
  } catch {
    return {};
  }
}

function writeStored(overrides: SettingsOverrides): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    /* Приватный режим Safari умеет запрещать запись — не повод падать. */
  }
}

/** Чем настройки отличаются от дефолтов сервиса. */
function diff(
  settings: PlannerSettings,
  defaults: PlannerSettings,
): SettingsOverrides {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(defaults) as Array<keyof PlannerSettings>) {
    if (settings[key] !== defaults[key]) out[key] = settings[key];
  }
  return out as SettingsOverrides;
}

export interface SettingsApi {
  settings: PlannerSettings;
  /** Изменено ли хоть что-то относительно серверных дефолтов. */
  dirty: boolean;
  patch(next: SettingsOverrides): void;
  reset(): void;
}

export function useSettings(defaults: PlannerSettings): SettingsApi {
  const [settings, setSettings] = useState<PlannerSettings>(() => ({
    ...defaults,
    ...readStored(defaults),
  }));

  const pending = useRef<SettingsOverrides>({});
  const timer = useRef<number | undefined>(undefined);

  const flush = useCallback(() => {
    const patchToSend = pending.current;
    pending.current = {};
    if (Object.keys(patchToSend).length === 0) return;

    void api
      .putSettings(patchToSend)
      .then((payload) => {
        if (payload.settings) setSettings(payload.settings);
      })
      .catch(() => {
        // Офлайн — правки уже применены локально и лягут на сервер при
        // следующем изменении.
      });
  }, []);

  // Подтягиваем настройки сессии: на другом устройстве их могли уже покрутить.
  useEffect(() => {
    let cancelled = false;
    void api
      .getSettings()
      .then((payload) => {
        if (cancelled || !payload.settings) return;
        setSettings(payload.settings);
        writeStored(diff(payload.settings, defaults));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [defaults]);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      flush();
    },
    [flush],
  );

  const patch = useCallback(
    (next: SettingsOverrides) => {
      setSettings((prev) => {
        const merged = { ...prev, ...next };
        writeStored(diff(merged, defaults));
        return merged;
      });
      pending.current = { ...pending.current, ...next };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, PUSH_DELAY_MS);
    },
    [defaults, flush],
  );

  const reset = useCallback(() => {
    setSettings(defaults);
    writeStored({});
    // Сервер хранит свою копию — отправляем полный набор дефолтов.
    pending.current = { ...defaults };
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, PUSH_DELAY_MS);
  }, [defaults, flush]);

  return {
    settings,
    dirty: Object.keys(diff(settings, defaults)).length > 0,
    patch,
    reset,
  };
}
